import { describeDomains, noArgsSchema } from "../operation-meta.js";
import { globalDocumentIDQueryArgsSchema, globalDocumentLinksByEntityQueryArgsSchema, studioIDQueryArgsSchema, } from "../projects-input-schemas.js";
import { ForbiddenError } from "../../errors.js";
import { canSeeAdminOnlyContent } from "../projects-helpers.js";
import { canReadProject, isStaff } from "../project-access.js";
import { assertCanEditAllProjects } from "./collaboration.js";
function visibleGlobalDocuments(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx))
        return projectZql.globalDocuments;
    return projectZql.globalDocuments.where("bucket", "team");
}
function visibleGlobalDocumentLinks(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx))
        return projectZql.globalDocumentLinks;
    return projectZql.globalDocumentLinks.whereExists("globalDocument", (doc) => doc.where("bucket", "team"));
}
/** Link types whose `linkID` is a project: project notes and debriefings. */
const PROJECT_LINK_TYPES = ["project", "project_note", "project_debriefing"];
function isProjectLinkType(linkType) {
    return !!linkType && PROJECT_LINK_TYPES.includes(linkType);
}
/** Restricts links to the projects a freelancer collaborates on. */
function scopeProjectLinks(ctx, links) {
    if (isStaff(ctx))
        return links;
    if (ctx.role !== "freelancer")
        return links.where("id", "__unauthorized__");
    const scoped = links.where("linkType", "IN", PROJECT_LINK_TYPES);
    if (ctx.clientSide)
        return scoped;
    const ids = ctx.readableProjectIDs ?? [];
    return ids.length > 0
        ? scoped.where("linkID", "IN", [...ids])
        : links.where("id", "__unauthorized__");
}
/**
 * Staff see the Docs tree, freelancers only documents linked to projects
 * they collaborate on (project notes and debriefings).
 */
function readableGlobalDocuments(ctx, projectZql) {
    if (isStaff(ctx)) {
        return visibleGlobalDocuments(ctx, projectZql).related("links");
    }
    if (ctx.role !== "freelancer") {
        return projectZql.globalDocuments.where("id", "__unauthorized__");
    }
    return visibleGlobalDocuments(ctx, projectZql)
        .whereExists("links", (links) => scopeProjectLinks(ctx, links))
        .related("links", (links) => scopeProjectLinks(ctx, links));
}
function assertProjectDocumentWriteAccess(ctx, doc, links, { allowOwnUnlinked = false } = {}) {
    if (!doc ||
        doc.bucket !== "team" ||
        doc.isFolder ||
        !doc.hiddenFromDocs ||
        links.some((link) => !isProjectLinkType(link.linkType))) {
        throw new ForbiddenError("Not allowed to edit documents");
    }
    // A freelancer's own fresh document may get its first link.
    if (allowOwnUnlinked &&
        links.length === 0 &&
        !!ctx.userID &&
        doc.createdBy === ctx.userID) {
        return;
    }
    assertCanEditAllProjects(ctx, links.map((link) => link.linkID), "Not allowed to edit documents");
}
async function loadDocumentWithLinks(tx, projectZql, globalDocumentID) {
    const doc = await tx.run(projectZql.globalDocuments
        .where("id", globalDocumentID)
        .related("links")
        .one());
    return {
        doc,
        links: (doc?.links ?? []),
    };
}
function assertBucketAccess(ctx, bucket) {
    if (bucket === "managers" && !canSeeAdminOnlyContent(ctx)) {
        throw new ForbiddenError("Not allowed to access the managers bucket");
    }
}
/** Every folder/document below `rootID` in the Docs tree. */
async function globalDocumentDescendants(tx, projectZql, rootID) {
    const rows = (await tx.run(projectZql.globalDocuments));
    const childrenByParent = new Map();
    for (const row of rows) {
        if (!row.parentID)
            continue;
        const children = childrenByParent.get(row.parentID) ?? [];
        children.push(row);
        childrenByParent.set(row.parentID, children);
    }
    const result = [];
    const queue = [...(childrenByParent.get(rootID) ?? [])];
    const seen = new Set([rootID]);
    while (queue.length > 0) {
        const row = queue.shift();
        if (seen.has(row.id))
            continue;
        seen.add(row.id);
        result.push(row);
        queue.push(...(childrenByParent.get(row.id) ?? []));
    }
    return result;
}
export function createDocumentQueries({ defineProjectQuery, projectZql, }) {
    if (!defineProjectQuery) {
        throw new Error("Missing document query dependencies");
    }
    return {
        studioDocuments: {
            all: defineProjectQuery({
                input: noArgsSchema,
                description: "Documents attached to studios, sorted by title, with document and studio. Admins and employees only. Read a document's body via GET /api/v1/documents/{documentID}/markdown.",
            }, ({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studioDocuments.where("id", "__unauthorized__");
                }
                return projectZql.studioDocuments
                    .related("document")
                    .related("studio")
                    .orderBy("title", "asc");
            }),
            byStudio: defineProjectQuery(studioIDQueryArgsSchema, ({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studioDocuments.where("id", "__unauthorized__");
                }
                return projectZql.studioDocuments
                    .where("studioID", args.studioID)
                    .related("document")
                    .related("studio")
                    .orderBy("title", "asc");
            }),
        },
        globalDocuments: {
            all: defineProjectQuery({
                input: noArgsSchema,
                description: "Every entry of the Docs tree (folders and documents, including ones hidden from the Docs page) with its document and links, in tree sort order. Admins see the team and managers buckets, employees only the team bucket, freelancers only team documents linked to projects they collaborate on, others none. Read a document's body via GET /api/v1/documents/{documentID}/markdown.",
            }, ({ ctx }) => readableGlobalDocuments(ctx, projectZql)
                .related("document")
                .orderBy("sortOrder", "asc")
                .orderBy("title", "asc")),
            list: defineProjectQuery(noArgsSchema, ({ ctx }) => readableGlobalDocuments(ctx, projectZql)
                .where("hiddenFromDocs", false)
                .related("document")
                .orderBy("sortOrder", "asc")
                .orderBy("title", "asc")),
        },
        globalDocumentLinks: {
            byEntity: defineProjectQuery(globalDocumentLinksByEntityQueryArgsSchema, ({ args, ctx, }) => {
                const allowed = isStaff(ctx)
                    ? true
                    : isProjectLinkType(args.linkType) &&
                        canReadProject(ctx, args.linkID);
                if (!allowed) {
                    return projectZql.globalDocumentLinks.where("id", "__unauthorized__");
                }
                return visibleGlobalDocumentLinks(ctx, projectZql)
                    .where("linkType", args.linkType)
                    .where("linkID", args.linkID)
                    .orderBy("createdAt", "asc");
            }),
            byDocument: defineProjectQuery(globalDocumentIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeProjectLinks(ctx, visibleGlobalDocumentLinks(ctx, projectZql))
                    .where("globalDocumentID", args.documentID)
                    .orderBy("createdAt", "asc");
            }),
        },
    };
}
export function createDocumentMutators({ defineProjectMutator, projectZql, schemas, assertAdminDataWriteAccess, }) {
    if (!defineProjectMutator || !schemas || !assertAdminDataWriteAccess) {
        throw new Error("Missing document mutator dependencies");
    }
    return {
        studioDocuments: {
            create: defineProjectMutator(schemas.createStudioDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.documents.insert({
                    id: args.documentID,
                    createdAt: now,
                    updatedAt: now,
                });
                await tx.mutate.studioDocuments.insert({
                    id: args.id,
                    title: args.title,
                    studioID: args.studioID,
                    documentID: args.documentID,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(schemas.updateStudioDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.studioDocuments.update({
                    id: args.id,
                    title: args.title,
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.studioDocumentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const doc = await tx.run(projectZql.studioDocuments.where("id", args.id).one());
                await tx.mutate.studioDocuments.delete({ id: args.id });
                if (doc?.documentID) {
                    await tx.mutate.documents.delete({ id: doc.documentID });
                }
            }),
        },
        globalDocuments: {
            create: defineProjectMutator({
                input: schemas.createGlobalDocumentInputSchema,
                description: "Create a global document or folder in the document tree. Pass client-generated `id` and (for documents) `documentID`; write the body afterwards with `PUT /api/v1/documents/{documentID}/markdown`.",
            }, async ({ tx, ctx, args, }) => {
                if (isStaff(ctx)) {
                    assertAdminDataWriteAccess(ctx);
                }
                else {
                    // Freelancers create the hidden document of a project note; it
                    // is linked to the project right after (globalDocumentLinks.create).
                    if (args.parentID ||
                        args.isFolder ||
                        !args.hiddenFromDocs ||
                        (args.bucket && args.bucket !== "team") ||
                        ctx.role !== "freelancer" ||
                        (!ctx.clientSide && (ctx.editableProjectIDs ?? []).length === 0)) {
                        throw new ForbiddenError("Not allowed to edit documents");
                    }
                }
                const parent = args.parentID
                    ? await tx.run(projectZql.globalDocuments.where("id", args.parentID).one())
                    : null;
                // Items are created in their folder's bucket.
                const bucket = parent?.bucket ?? args.bucket ?? "team";
                if (args.bucket && args.bucket !== bucket) {
                    throw new Error("Documents must be in the same bucket as their folder");
                }
                if (args.hiddenFromDocs && bucket !== "team") {
                    throw new Error("Linked documents must stay in the team bucket");
                }
                assertBucketAccess(ctx, bucket);
                const now = Date.now();
                const documentID = args.isFolder ? null : args.documentID;
                if (documentID) {
                    await tx.mutate.documents.insert({
                        id: documentID,
                        createdAt: now,
                        updatedAt: now,
                    });
                }
                await tx.mutate.globalDocuments.insert({
                    id: args.id,
                    title: args.title,
                    parentID: args.parentID ?? null,
                    isFolder: args.isFolder ?? false,
                    hiddenFromDocs: args.hiddenFromDocs ?? false,
                    bucket,
                    documentID,
                    sortOrder: 0,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator({
                input: schemas.updateGlobalDocumentInputSchema,
                description: "Rename, move or re-bucket a global document.",
            }, async ({ tx, ctx, args, }) => {
                if (isStaff(ctx)) {
                    assertAdminDataWriteAccess(ctx);
                }
                else {
                    // Freelancers may only rename project documents.
                    const { doc, links } = await loadDocumentWithLinks(tx, projectZql, args.id);
                    assertProjectDocumentWriteAccess(ctx, doc, links);
                    if (args.parentID !== undefined ||
                        args.bucket !== undefined ||
                        args.sortOrder !== undefined) {
                        throw new ForbiddenError("Not allowed to move documents");
                    }
                }
                const existing = await tx.run(projectZql.globalDocuments.where("id", args.id).one());
                assertBucketAccess(ctx, existing?.bucket);
                const parent = args.parentID
                    ? await tx.run(projectZql.globalDocuments.where("id", args.parentID).one())
                    : null;
                const currentBucket = existing?.bucket ?? "team";
                const bucket = parent?.bucket ?? args.bucket ?? currentBucket;
                if (args.bucket && args.bucket !== bucket) {
                    throw new Error("Documents must be in the same bucket as their folder");
                }
                assertBucketAccess(ctx, bucket);
                const updates = { ...args, bucket };
                const movesBucket = bucket !== currentBucket;
                const descendants = movesBucket
                    ? await globalDocumentDescendants(tx, projectZql, args.id)
                    : [];
                if (movesBucket) {
                    // Linked docs (project notes, SIM docs) must never disappear
                    // from employees, also not as part of a moved folder.
                    if (existing?.hiddenFromDocs ||
                        descendants.some((row) => row.hiddenFromDocs)) {
                        throw new Error("Linked documents must stay in the team bucket");
                    }
                    // Moving to the other bucket without a target folder lands at
                    // its root: the old parent belongs to the old bucket.
                    if (args.parentID === undefined)
                        updates.parentID = null;
                }
                const now = Date.now();
                await tx.mutate.globalDocuments.update({
                    ...updates,
                    updatedAt: now,
                });
                // A folder moves to another bucket together with its contents.
                for (const row of descendants) {
                    await tx.mutate.globalDocuments.update({
                        id: row.id,
                        bucket,
                        updatedAt: now,
                    });
                }
            }),
            delete: defineProjectMutator({
                input: schemas.globalDocumentIDInputSchema,
                description: "Hard-delete a global document and its whole subtree, including the bodies.",
                destructive: true,
            }, async ({ tx, ctx, args, }) => {
                if (isStaff(ctx)) {
                    assertAdminDataWriteAccess(ctx);
                }
                else {
                    const { doc, links } = await loadDocumentWithLinks(tx, projectZql, args.id);
                    assertProjectDocumentWriteAccess(ctx, doc, links);
                }
                const doc = await tx.run(projectZql.globalDocuments.where("id", args.id).one());
                assertBucketAccess(ctx, doc?.bucket);
                await tx.mutate.globalDocuments.delete({ id: args.id });
                if (doc?.documentID) {
                    await tx.mutate.documents.delete({ id: doc.documentID });
                }
            }),
        },
        globalDocumentLinks: {
            create: defineProjectMutator(schemas.createGlobalDocumentLinkInputSchema, async ({ tx, ctx, args, }) => {
                if (isStaff(ctx)) {
                    assertAdminDataWriteAccess(ctx);
                }
                else {
                    const { doc, links } = await loadDocumentWithLinks(tx, projectZql, args.globalDocumentID);
                    assertProjectDocumentWriteAccess(ctx, doc, links, {
                        allowOwnUnlinked: true,
                    });
                    if (!isProjectLinkType(args.linkType)) {
                        throw new ForbiddenError("Not allowed to edit documents");
                    }
                    assertCanEditAllProjects(ctx, [args.linkID], "Not allowed to edit documents");
                }
                const doc = await tx.run(projectZql.globalDocuments.where("id", args.globalDocumentID).one());
                assertBucketAccess(ctx, doc?.bucket);
                const existing = await tx.run(projectZql.globalDocumentLinks
                    .where("globalDocumentID", args.globalDocumentID)
                    .where("linkType", args.linkType)
                    .where("linkID", args.linkID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.globalDocumentLinks.insert({
                    ...args,
                    createdAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.deleteGlobalDocumentLinkInputSchema, async ({ tx, ctx, args, }) => {
                if (isStaff(ctx)) {
                    assertAdminDataWriteAccess(ctx);
                }
                else {
                    const { doc, links } = await loadDocumentWithLinks(tx, projectZql, args.globalDocumentID);
                    if (!links.some((link) => link.id === args.id)) {
                        throw new ForbiddenError("Not allowed to edit documents");
                    }
                    assertProjectDocumentWriteAccess(ctx, doc, links);
                }
                await tx.mutate.globalDocumentLinks.delete({ id: args.id });
            }),
        },
    };
}
export const documentDomainDescriptions = describeDomains({
    studioDocuments: "Documentation pages attached to a studio",
    globalDocuments: "Wiki-style document tree (team and managers buckets)",
    globalDocumentLinks: "Links between global documents and other records",
});
